import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, FileText, Upload, Plus, Trash2, PenTool, ShieldCheck, Type, Sparkles } from 'lucide-react';

// Preset regex patterns for common Indian document identifiers.
// Selecting a preset auto-fills the pattern + error message so admins
// don't have to remember the regex.
const FIELD_PRESETS = [
  {
    id: 'pan',
    label: 'PAN Card',
    pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$',
    error_message: 'Please enter a valid 10-digit PAN (e.g. ABCDE1234F)',
  },
  {
    id: 'aadhaar',
    label: 'Aadhaar Number',
    pattern: '^[0-9]{12}$',
    error_message: 'Please enter a valid 12-digit Aadhaar number',
  },
  {
    id: 'mobile',
    label: 'Indian Mobile',
    pattern: '^[6-9][0-9]{9}$',
    error_message: 'Please enter a valid 10-digit mobile number',
  },
  {
    id: 'gstin',
    label: 'GSTIN',
    pattern: '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
    error_message: 'Please enter a valid 15-character GSTIN',
  },
  {
    id: 'ifsc',
    label: 'IFSC Code',
    pattern: '^[A-Z]{4}0[A-Z0-9]{6}$',
    error_message: 'Please enter a valid 11-character IFSC code',
  },
  {
    id: 'pincode',
    label: 'Pincode',
    pattern: '^[1-9][0-9]{5}$',
    error_message: 'Please enter a valid 6-digit pincode',
  },
];

const DEFAULT_NEW_FIELD = {
  name: '',
  label: '',
  type: 'text',
  validation_mode: 'free', // 'free' | 'fixed'
  pattern: '',
  error_message: '',
};

export default function FormMaster() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [fields, setFields] = useState([]);
  const [uploads, setUploads] = useState([]);

  const [newField, setNewField] = useState({ ...DEFAULT_NEW_FIELD });
  const [isSeeding, setIsSeeding] = useState(false);

  const [newUpload, setNewUpload] = useState({
    name: '',
    label: '',
    allowed_types: '*',
    max_size_mb: 5
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/form-master`);
      setFields(res.data.fields || []);
      setUploads(res.data.uploads || []);
    } catch (err) {
      toast.error('Failed to load Form Master data');
    }
  };

  // All write endpoints under /api/form-master require the admin JWT.
  // Read it fresh on every call so a re-login is picked up automatically.
  const authHeaders = () => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('userToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchData();
  }, [API_URL]);

  const handlePresetChange = (presetId) => {
    if (!presetId) {
      setNewField((f) => ({ ...f, pattern: '', error_message: '' }));
      return;
    }
    const preset = FIELD_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    setNewField((f) => ({
      ...f,
      pattern: preset.pattern,
      error_message: preset.error_message,
    }));
  };

  const handleAddField = async (e) => {
    e.preventDefault();
    if (!newField.name || !newField.label) return toast.error('Name and Label are required');

    // Validate regex syntax when Fixed Format is selected so we don't
    // persist a broken pattern that would silently allow anything.
    const isFixed = newField.validation_mode === 'fixed';
    if (isFixed) {
      if (!newField.pattern.trim()) {
        return toast.error('Fixed Format requires a Regex Pattern');
      }
      try {
        // eslint-disable-next-line no-new
        new RegExp(newField.pattern);
      } catch (_err) {
        return toast.error('Invalid regex pattern');
      }
      if (!newField.error_message.trim()) {
        return toast.error('Fixed Format requires an Error Message');
      }
    }

    const snakeName = newField.name.toLowerCase().replace(/\s+/g, '_');
    const payload = {
      name: snakeName,
      label: newField.label,
      type: newField.type,
      pattern: isFixed ? newField.pattern.trim() : null,
      error_message: isFixed ? newField.error_message.trim() : null,
    };

    try {
      await axios.post(`${API_URL}/api/form-master/fields`, payload, { headers: authHeaders() });
      toast.success('Field added');
      setNewField({ ...DEFAULT_NEW_FIELD });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add field');
    }
  };

  const handleAddUpload = async (e) => {
    e.preventDefault();
    if (!newUpload.name || !newUpload.label) return toast.error('Name and Label are required');
    const snakeName = newUpload.name.toLowerCase().replace(/\s+/g, '_');
    try {
      await axios.post(
        `${API_URL}/api/form-master/uploads`,
        { ...newUpload, name: snakeName },
        { headers: authHeaders() }
      );
      toast.success('Upload document added');
      setNewUpload({ name: '', label: '', allowed_types: '*', max_size_mb: 5 });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add upload document');
    }
  };

  const handleDeleteField = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/form-master/fields/${id}`, { headers: authHeaders() });
      toast.success('Field deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete field');
    }
  };

  const handleSeedPresets = async () => {
    if (!window.confirm('This will add 6 common Indian ID fields (PAN, Aadhaar, GSTIN, IFSC, Pincode, Mobile) with their regex patterns. Existing fields with the same name will be updated. Continue?')) return;
    setIsSeeding(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/form-master/fields/seed-presets`,
        {},
        { headers: authHeaders() }
      );
      const { inserted = 0, updated = 0 } = res.data || {};
      toast.success(`Seeded ${inserted} new + updated ${updated} existing ID fields`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to seed preset fields');
    } finally {
      setIsSeeding(false);
    }
  };

  const handleDeleteUpload = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/form-master/uploads/${id}`, { headers: authHeaders() });
      toast.success('Upload document deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete upload document');
    }
  };

  const isFixed = newField.validation_mode === 'fixed';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-bold mb-6 text-[#172554] flex items-center gap-2">
        <Settings className="w-5 h-5 text-gray-500" /> Form Master Configuration
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

        {/* Form Fields Section */}
        <div>
          <h4 className="font-bold text-[#172554] border-b pb-2 mb-4 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Input Fields</span>
            <button
              type="button"
              onClick={handleSeedPresets}
              disabled={isSeeding}
              data-testid="form-master-seed-presets-btn"
              className="inline-flex items-center gap-1.5 bg-[#F5B800] hover:bg-[#FFC928] text-[#172554] text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-colors disabled:opacity-60"
              title="Seed common Indian ID fields (PAN, Aadhaar, GSTIN, IFSC, Pincode, Mobile)"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {isSeeding ? 'Seeding…' : 'Seed Common ID Fields'}
            </button>
          </h4>

          <form onSubmit={handleAddField} className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 space-y-3" data-testid="form-master-add-field-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Field Label (e.g. PAN Card)</label>
                <input
                  type="text" required
                  data-testid="form-master-field-label-input"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={newField.label}
                  onChange={(e) => {
                    setNewField({ ...newField, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') });
                  }}
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Internal Name</label>
                <input
                  type="text" required
                  data-testid="form-master-field-name-input"
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none bg-gray-100 font-mono"
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Input Type</label>
              <select
                data-testid="form-master-field-type-select"
                className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                value={newField.type}
                onChange={(e) => setNewField({ ...newField, type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="date">Date</option>
                <option value="signature">Signature (સહી)</option>
              </select>
            </div>

            {/* Validation mode radio */}
            <div className="pt-2">
              <label className="text-xs font-bold text-gray-600 block mb-2">Validation Mode</label>
              <div className="grid grid-cols-2 gap-2">
                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    !isFixed ? 'bg-blue-50 border-blue-500' : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="validation-mode-free-radio"
                >
                  <input
                    type="radio"
                    name="validation_mode"
                    value="free"
                    checked={!isFixed}
                    onChange={() => setNewField({ ...newField, validation_mode: 'free', pattern: '', error_message: '' })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-bold text-[#172554] flex items-center gap-1"><Type className="w-3.5 h-3.5" /> Free Text</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Any alphanumeric input allowed</div>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    isFixed ? 'bg-yellow-50 border-[#F5B800]' : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="validation-mode-fixed-radio"
                >
                  <input
                    type="radio"
                    name="validation_mode"
                    value="fixed"
                    checked={isFixed}
                    onChange={() => setNewField({ ...newField, validation_mode: 'fixed' })}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-bold text-[#172554] flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Fixed Format</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">Enforce a regex pattern (PAN, Aadhaar…)</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed-format only inputs */}
            {isFixed && (
              <div className="space-y-3 border-t border-yellow-200 pt-3" data-testid="fixed-format-fields-block">
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Quick Preset (optional)</label>
                  <select
                    data-testid="form-master-preset-select"
                    className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                    onChange={(e) => handlePresetChange(e.target.value)}
                    defaultValue=""
                  >
                    <option value="">— Use a common preset —</option>
                    {FIELD_PRESETS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Validation Pattern (Regex) *</label>
                  <input
                    type="text" placeholder="e.g. ^[A-Z]{5}[0-9]{4}[A-Z]{1}$"
                    data-testid="form-master-pattern-input"
                    className="w-full border border-gray-300 rounded p-2 text-sm outline-none font-mono focus:border-blue-500"
                    value={newField.pattern}
                    onChange={(e) => setNewField({ ...newField, pattern: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 block mb-1">Error Message *</label>
                  <input
                    type="text" placeholder="e.g. Please enter a valid 10-digit PAN (e.g. ABCDE1234F)"
                    data-testid="form-master-error-message-input"
                    className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                    value={newField.error_message}
                    onChange={(e) => setNewField({ ...newField, error_message: e.target.value })}
                  />
                </div>
              </div>
            )}

            <button type="submit" data-testid="form-master-add-field-submit" className="w-full bg-[#172554] text-white py-2 rounded text-sm font-bold flex items-center justify-center gap-1 hover:bg-blue-900 transition-colors">
              <Plus className="w-4 h-4" /> Add Field
            </button>
          </form>

          <ul className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto border rounded-lg">
            {(fields || []).map(f => (
              <li key={f.id} className="p-3 hover:bg-gray-50 flex justify-between items-start" data-testid={`form-master-field-item-${f.name}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#172033] text-sm flex items-center gap-2">
                    {f.label}
                    {f.pattern ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded uppercase tracking-wide">
                        <ShieldCheck className="w-3 h-3" /> Fixed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded uppercase tracking-wide">
                        <Type className="w-3 h-3" /> Free
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1">Name: {f.name} | Type: {f.type} {f.type === 'signature' && <PenTool className="w-3 h-3 inline text-blue-600" />}</div>
                  {f.pattern && (
                    <div className="text-[10px] text-blue-700 font-mono mt-1 bg-blue-50 px-1 py-0.5 rounded w-fit break-all">Regex: {f.pattern}</div>
                  )}
                  {f.error_message && (
                    <div className="text-[10px] text-red-600 mt-1 italic">“{f.error_message}”</div>
                  )}
                </div>
                <button onClick={() => handleDeleteField(f.id)} className="text-red-500 p-1 hover:bg-red-50 rounded shrink-0" data-testid={`form-master-delete-field-${f.name}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
            {fields.length === 0 && <li className="p-4 text-center text-sm text-gray-500">No fields configured</li>}
          </ul>
        </div>

        {/* Uploads Section */}
        <div>
          <h4 className="font-bold text-[#172554] border-b pb-2 mb-4 flex items-center gap-2">
            <Upload className="w-4 h-4" /> Document Uploads
          </h4>

          <form onSubmit={handleAddUpload} className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6 space-y-3">
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Document Label (e.g. Ration Card)</label>
              <input
                type="text" required
                className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                value={newUpload.label}
                onChange={(e) => {
                  setNewUpload({ ...newUpload, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') });
                }}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 block mb-1">Internal Name</label>
              <input
                type="text" required
                className="w-full border border-gray-300 rounded p-2 text-sm outline-none bg-gray-100 font-mono"
                value={newUpload.name}
                onChange={(e) => setNewUpload({ ...newUpload, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Document Format</label>
                <select
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={newUpload.allowed_types}
                  onChange={(e) => setNewUpload({ ...newUpload, allowed_types: e.target.value })}
                >
                  <option value="*">Any (PDF or Image)</option>
                  <option value="image/jpeg,image/png,image/jpg">Images Only (JPG/PNG)</option>
                  <option value="application/pdf">PDF Only</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 block mb-1">Max Size (MB)</label>
                <input
                  type="number" min="1" max="100" required
                  className="w-full border border-gray-300 rounded p-2 text-sm outline-none focus:border-blue-500"
                  value={newUpload.max_size_mb}
                  onChange={(e) => setNewUpload({ ...newUpload, max_size_mb: parseInt(e.target.value) || 5 })}
                />
              </div>
            </div>

            <button type="submit" className="w-full bg-[#172554] text-white py-2 rounded text-sm font-bold flex items-center justify-center gap-1 hover:bg-blue-900 transition-colors">
              <Plus className="w-4 h-4" /> Add Document
            </button>
          </form>

          <ul className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto border rounded-lg">
            {(uploads || []).map(u => (
              <li key={u.id} className="p-3 hover:bg-gray-50 flex justify-between items-center">
                <div>
                  <div className="font-bold text-[#172033] text-sm">{u.label}</div>
                  <div className="text-xs text-gray-500 font-mono mt-1">Name: {u.name}</div>
                  <div className="text-[10px] text-purple-600 font-mono mt-1 bg-purple-50 px-1 py-0.5 rounded w-fit">
                    Format: {u.allowed_types === '*' ? 'Any' : (u.allowed_types.includes('pdf') ? 'PDF' : 'Images')} | Max: {u.max_size_mb}MB
                  </div>
                </div>
                <button onClick={() => handleDeleteUpload(u.id)} className="text-red-500 p-1 hover:bg-red-50 rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
            {uploads.length === 0 && <li className="p-4 text-center text-sm text-gray-500">No documents configured</li>}
          </ul>
        </div>

      </div>
    </div>
  );
}
